import { Text, Layout, Page, LegacyStack, EmptyState, LegacyCard, Badge, Divider } from '@shopify/polaris';
import { useParams } from 'react-router';
import ProductsWrapper from '../../UI/Products/ProductsWrapper';
import TransferStockTable from '../../UI/Tables/TransferStockTable';
import AddStockCard from '../../UI/Transfers/AddStockCard';
import ReceiveStockCard from '../../UI/Transfers/ReceiveStockCard';
import ViewTransferPrimaryActions from '../../UI/Transfers/ViewTransferPrimaryActions';
import { useHistory } from 'react-router-dom';
import { useState } from 'react';
import { useQueryClient } from 'react-query';
import {
  useTransferQuery,
  useTransferStockQuery,
  transferKeys,
} from '../../../customHooks/useTransferQueries';
import { useTimezone } from '../../../customHooks/useTimezone';
import { displayInTz } from '../../../helpers/dates';

// Mapa estado → label para Badge + texto descriptivo. Antes esto vivía en
// useState calculados a mano dentro del componente; lo dejamos como mapa puro
// para que sea trivial de mantener.
const STATUS = {
  PENDING: {
    label: 'Pendiente',
    badgeStatus: 'warning',
    instructions: 'La transferencia está pendiente. Puedes añadir mercancía.',
  },
  IN_TRANSIT: {
    label: 'En tránsito',
    badgeStatus: 'info',
    instructions: 'La transferencia está en tránsito. Anota la mercancía recibida o cierra la transferencia.',
  },
  CLOSED: {
    label: 'Cerrado',
    badgeStatus: 'success',
    instructions: 'La transferencia ha sido cerrada.',
  },
};
const STATUS_UNKNOWN = { label: 'Desconocido', badgeStatus: 'attention', instructions: 'Estado desconocido.' };

const ViewTransfer = () => {
  const { id } = useParams();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { timezone } = useTimezone();

  const [page, setPage] = useState(1);

  const { data: transfer } = useTransferQuery(id);
  const { data: stockResp } = useTransferStockQuery(id, { page, limit: 10 });
  // ProductsWrapper acumula páginas internamente y se reinicia cuando
  // recibe `new: true`. Lo marcamos así en la página 1 para mantener el
  // comportamiento previo cuando el usuario entra o se invalida el stock.
  const stock = stockResp ? { ...stockResp, new: page === 1 } : null;

  // AddStockCard llama al servicio directamente (no a través de la mutación
  // de react-query), así que invalidamos manualmente el detalle y el stock
  // cuando avisa de que ha añadido. Si en una sesión futura se migra
  // AddStockCard a useTransferMutations, esto deja de hacer falta.
  const handleStockAdded = () => {
    queryClient.invalidateQueries(transferKeys.detail(id));
    queryClient.invalidateQueries(['transfer-stock', Number(id)]);
  };

  if (!transfer) return null;

  const status = STATUS[transfer.status] || STATUS_UNKNOWN;
  const canAddH = transfer.status === 'PENDING';
  const canReceive = transfer.status === 'IN_TRANSIT';
  const returnUrl = `/transfers`;

  const TableComponent = (args) => <TransferStockTable {...args} transfer={transfer} />;

  return (
    <Page
      titleMetadata={<Badge status={status.badgeStatus}>{status.label}</Badge>}
      backAction={{ content: 'Volver', onAction: () => history.goBack() }}
      title={transfer.name}
      subtitle={
        <div className="flex flex-col">
          <Text variant="bodySm" as="span" color="subdued" className="block">
            {`${transfer.originDeposit.name} ⇢ ${transfer.destinationDeposit.name}`}
          </Text>
          <Text variant="bodySm" as="span" color="subdued" className="block">
            {`Traspaso: ${displayInTz(transfer.createdAt, timezone) || 'sin fecha'} · Cierre: ${
              transfer.closedAt ? displayInTz(transfer.closedAt, timezone) : 'sin cerrar'
            }`}
          </Text>
        </div>
      }
      primaryAction={<ViewTransferPrimaryActions transfer={transfer} returnUrl={returnUrl} />}
      divider
    >
      <Layout>
        <TransferNumbers transfer={transfer} instructions={status.instructions} />

        {canAddH && (
          <Layout.Section>
            <AddStockCard transfer={transfer} onStockAdded={handleStockAdded} />
          </Layout.Section>
        )}

        {canReceive && (
          <Layout.Section>
            <ReceiveStockCard transfer={transfer} />
          </Layout.Section>
        )}

        <Layout.Section>
          {stock && !stock.data.length && (
            <LegacyCard>
              <LegacyCard.Section>
                <EmptyState
                  heading="Todavía no se ha añadido mercancía"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                />
              </LegacyCard.Section>
            </LegacyCard>
          )}
          {stock && stock.data.length > 0 && (
            <ProductsWrapper data={stock} TableComponent={TableComponent} loadPage={() => setPage(page + 1)} />
          )}
        </Layout.Section>
      </Layout>
    </Page>
  );
};

export default ViewTransfer;

const TransferNumbers = ({ transfer, instructions }) => {
  const TableRich = () => (
    <div className="grid grid-cols-[auto_1fr_1fr_1fr] gap-4 items-center">
      <div></div>
      <Text variant="headingXs" weight="semibold" as="h3" alignment="center">
        Uds
      </Text>
      <Text variant="headingXs" weight="semibold" alignment="center">
        SKU
      </Text>
      <Text variant="headingXs" weight="semibold" alignment="center">
        Serie-Color
      </Text>

      <Text variant="headingSm" weight="semibold" alignment="end">
        Enviado
      </Text>
      <Text variant="headingXl" alignment="center">
        {transfer.stockQty}
      </Text>
      <Text variant="headingXl" alignment="center">
        {transfer.variantsQty}
      </Text>
      <Text variant="headingXl" alignment="center">
        {transfer.productsQty}
      </Text>
      <Text variant="headingSm" weight="semibold" alignment="end">
        Recibido
      </Text>
      <Text variant="headingXl" alignment="center">
        {transfer.stockReceivedQty}
      </Text>
      <Text variant="headingXl" alignment="center">
        {transfer.variantsReceivedQty}
      </Text>
      <Text variant="headingXl" alignment="center">
        {transfer.productsReceivedQty}
      </Text>

      {/* Solo cuando hay merma: así la resta entre enviado y recibido no queda sin explicar. */}
      {transfer.stockLostQty > 0 && (
        <>
          <Text variant="headingSm" weight="semibold" alignment="end" color="critical">
            Extraviado
          </Text>
          <Text variant="headingXl" alignment="center" color="critical">
            {transfer.stockLostQty}
          </Text>
          <Text variant="headingXl" alignment="center" color="subdued">
            &nbsp;
          </Text>
          <Text variant="headingXl" alignment="center" color="subdued">
            &nbsp;
          </Text>
        </>
      )}
    </div>
  );

  const TableLegacy = () => (
    <LegacyStack distribution="fill">
      <span>
        <Text variant="headingXs" weight="semibold" as="h3">
          Uds
        </Text>
        <Text variant="heading3xl">{transfer.stockQty}</Text>
      </span>
      <span>
        <Text variant="headingXs" weight="semibold">
          Serie-Color
        </Text>
        <Text variant="heading3xl">{transfer.productsQty}</Text>
      </span>
      <span>
        <Text variant="headingXs" weight="semibold">
          SKU
        </Text>
        <Text variant="heading3xl">{transfer.variantsQty}</Text>
      </span>
    </LegacyStack>
  );

  return (
    <Layout.Section>
      <LegacyCard>
        <LegacyCard.Section>
          <div className="flex flex-col gap-4">
            {transfer.status === 'PENDING' ? <TableLegacy /> : <TableRich />}
            <Divider />
            <Text variant="bodyMd" className="block">
              {instructions}
            </Text>
          </div>
        </LegacyCard.Section>
      </LegacyCard>
    </Layout.Section>
  );
};
